import {
  GridCellParams,
  GridColDef,
  GridColumnVisibilityModel,
  GridCallbackDetails,
  GridEventListener,
} from '@mui/x-data-grid';
import { useMemo, useState } from 'react';
import {
  AllSitesAlarmType,
  SiteAlarmType,
} from '../../../features/alarms/alarmsAPI';
import {
  convertJSDateToDateTimeString,
  convertISOToJSDate,
} from '../../../utils/time';
import DataGrid from '../../DataGrid/DataGrid';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';
import { useNavigate, useLocation } from 'react-router-dom';
import { DateTime } from 'luxon';
// import AssignAlarmAction from "./AssignAlarmAction";
import useSyncSiteAlarms from '../useSyncSiteAlarms';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { AlarmLeoAlertStatus } from '../../../features/alarms/alarmsAPI';
import CircularProgress from '@mui/material/CircularProgress';
import { DateFilterOperators } from '../../DataGrid/filterOperators';
import { useTheme, useMediaQuery } from '@mui/material';
import AlarmInfoDialog from '../AlarmInfoDialog';
import buildGraphingUrl from '../utils/buildGraphingUrl';

type SiteAlarmsProps = {
  alarms: SiteAlarmType[];
  siteId: number;
  isLoading: boolean;
  leoAlertAvailable: boolean;
  syncAlarmsAvailable: boolean;
  refetch: () => void;
  removeTopMargin?: boolean;
};

type AllSitesAlarmsProps = {
  alarms: AllSitesAlarmType[];
  siteId?: never;
  isLoading: boolean;
  leoAlertAvailable?: boolean;
  syncAlarmsAvailable?: never;
  refetch?: never;
  removeTopMargin?: boolean;
};

type Props = SiteAlarmsProps | AllSitesAlarmsProps;

function ActiveAlarmsTable({
  alarms,
  isLoading,
  removeTopMargin,
  siteId,
  refetch,
  syncAlarmsAvailable,
  leoAlertAvailable = false,
}: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isSmallDevice = useMediaQuery(theme.breakpoints.down('sm'));

  const columns: GridColDef<SiteAlarmType | AllSitesAlarmType>[] =
    useMemo(() => {
      return [
        {
          headerName: 'Date/Time',
          field: 'alarm_date',
          flex: 0.5,
          minWidth: 120,
          align: 'left',
          headerAlign: 'left',
          valueGetter: (value: string) => {
            return convertISOToJSDate(value);
          },
          valueFormatter: (value: Date, row) => {
            return convertJSDateToDateTimeString(value, {
              zone: row.timezone,
            });
          },
          type: 'dateTime',
          filterOperators: DateFilterOperators,
        },
        {
          headerName: 'Description',
          field: 'alarm_text',
          flex: 1,
          minWidth: 200,
        },
        {
          headerName: 'Site Name',
          field: 'siteName',
          flex: 0.5,
          hideable: true,
          disableExport: siteId !== undefined,
        },
        {
          headerName: 'Controller',
          field: 'controller_name',
          flex: 0.5,
          minWidth: 150,
        },
        {
          renderHeader: () => (
            <strong>
              {'Source'}
              &nbsp;
              <span>
                <QueryStatsRoundedIcon />
              </span>
            </strong>
          ),
          field: 'alarm_source',
          flex: 1,
          minWidth: 200,

          cellClassName: (
            props: GridCellParams<SiteAlarmType | AllSitesAlarmType>,
          ) => {
            if (
              props.row.application === null ||
              props.row.application === '0'
            ) {
              return '';
            }
            return 'active-alarm_source-cell';
          },
        },
        {
          headerName: 'LEO Alert Sent',
          field: 'leoAlertStatus',
          type: 'boolean',
          minWidth: 120,
          disableExport: !leoAlertAvailable,
          renderCell: (params) => {
            const value = params.value as AlarmLeoAlertStatus | undefined;
            switch (value) {
              case 'SENT':
                return (
                  <CheckRoundedIcon
                    sx={{
                      color: (theme) =>
                        theme.palette.mode === 'dark' ? 'white' : 'black',
                    }}
                  />
                );
              case 'NOT SENT':
                return (
                  <CloseRoundedIcon
                    sx={{
                      color: (theme) =>
                        theme.palette.mode === 'dark' ? 'white' : 'black',
                    }}
                  />
                );
              case 'LOADING':
                return (
                  <CircularProgress size={20} sx={{ color: 'primary.main' }} />
                );
              default:
                return '';
            }
          },
        },
        // {
        //   type: "actions",
        //   field: "actions",
        //   maxWidth: 80,
        //   getActions: (params) => {
        //     return [<AssignAlarmAction key={"1"} row={params.row} />];
        //   },
        // },
      ];
    }, [leoAlertAvailable, siteId]);

  const tableData = useMemo(() => {
    if (alarms) {
      return alarms.map((d, i) => ({ ...d, id: i }));
    }

    return [];
  }, [alarms]);

  const [selectedAlarm, setSelectedAlarm] = useState<
    SiteAlarmType | AllSitesAlarmType | null
  >(null);
  const hideAlarmInfoDialog = () => setSelectedAlarm(null);

  const handleRowClick: GridEventListener<'rowClick'> = (
    params,
    _e,
    _details,
  ) => {
    setSelectedAlarm(params.row);
  };

  const handleCellClick: GridEventListener<'cellClick'> = (
    params: GridCellParams<SiteAlarmType | AllSitesAlarmType>,
    _event,
    _details,
  ) => {
    if (params.field === 'alarm_source') {
      // they clicked the alarm_source cell
      const { value, row } = params;
      // Needs a valid application to graph
      if (row.application !== null && row.application !== '0' && value) {
        const url = buildGraphingUrl(value as string, row);

        const startDate = DateTime.fromISO(row.alarm_date)
          .minus({ hours: 1 })
          .toUTC()
          .toISO();
        const endDate = DateTime.now().toUTC().toISO();

        navigate(url, {
          state: {
            graphRequestDates: {
              startDate,
              endDate,
            },
            from: location.pathname,
          },
        });
      }
    }
  };

  const SyncSiteAlarms = useSyncSiteAlarms({ siteId, refetch });

  const [columnVisibilityModel, setColumnVisibiltyModel] =
    useState<GridColumnVisibilityModel>({
      alarm_source: !isSmallDevice,
      leoAlertStatus: !isSmallDevice,
      siteName: !isSmallDevice,
    });

  const handleColumnVisibilityChange = (
    model: GridColumnVisibilityModel,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _details: GridCallbackDetails<any>,
  ) => {
    setColumnVisibiltyModel(model);
  };

  return (
    <>
      <DataGrid
        rows={tableData}
        columns={columns}
        loading={isLoading}
        removeTopMargin={removeTopMargin}
        getRowClassName={(params) => {
          if ((params.row as SiteAlarmType | AllSitesAlarmType).filtered) {
            if (params.indexRelativeToCurrentPage % 2 === 0) {
              return 'yellow-row';
            }
            return 'yellow-row--odd';
          }
          if (params.indexRelativeToCurrentPage % 2 === 0) {
            return 'red-row';
          }
          return 'red-row--odd';
        }}
        autoPageSize
        initialState={{
          sorting: {
            sortModel: [
              {
                field: 'alarm_date',
                sort: 'desc',
              },
              {
                field: 'siteName',
                sort: 'asc',
              },
            ],
          },
          columns: {
            columnVisibilityModel: {
              controller_name: !isSmallDevice,
            },
          },
        }}
        onColumnVisibilityModelChange={handleColumnVisibilityChange}
        columnVisibilityModel={{
          ...columnVisibilityModel,
          ...(siteId !== undefined && {
            siteName: false,
          }),
          ...(!leoAlertAvailable && {
            leoAlertStatus: false,
          }),
        }}
        sx={{
          // disable cell selection style
          '.MuiDataGrid-cell:focus': {
            outline: 'none',
          },
          // pointer cursor on ALL rows
          '& .MuiDataGrid-row:hover': {
            cursor: 'pointer',
          },
        }}
        onCellClick={handleCellClick}
        onRowClick={handleRowClick}
        quickFilter
        customButtons={
          syncAlarmsAvailable && SyncSiteAlarms ? [SyncSiteAlarms] : []
        }
      />
      {selectedAlarm && siteId !== undefined && (
        <AlarmInfoDialog
          open={true}
          onClose={hideAlarmInfoDialog}
          alarm={selectedAlarm as SiteAlarmType}
          siteId={siteId}
          leoAlertAvailable={leoAlertAvailable}
        />
      )}
      {selectedAlarm && siteId === undefined && (
        <AlarmInfoDialog
          open={true}
          onClose={hideAlarmInfoDialog}
          alarm={selectedAlarm as AllSitesAlarmType}
          siteId={siteId}
          leoAlertAvailable={leoAlertAvailable}
        />
      )}
    </>
  );
}

export default ActiveAlarmsTable;
